import logger from '../../utils/logger';

// DataForSEO Google Maps/Local Finder Live Advanced API integration
// Complete grid-based rank tracker implementation

// Backend API Endpoints (using backend proxy instead of direct DataForSEO calls)
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
const GOOGLE_MAPS_ENDPOINT = `${BACKEND_BASE_URL}/api/serp/maps`;
const LOCAL_FINDER_ENDPOINT = `${BACKEND_BASE_URL}/api/serp/local_finder`;

// No longer need credentials in frontend - handled by backend
const DATAFORSEO_LOGIN = null;
const DATAFORSEO_PASSWORD = null;

// Grid coordinate conversion functions (DataForSEO specification)
function metersToDegreesLat(meters) {
  return meters / 111320;
}

function metersToDegreesLng(meters, lat) {
  return meters / (111320 * Math.cos(lat * Math.PI / 180));
}

// Grid generation according to DataForSEO specifications
function generateGrid(centerLat, centerLng, gridSize, stepMeters, searchRadiusMeters = 800) {
  // Ensure grid_size is odd
  if (gridSize % 2 === 0) {
    logger.warn(`Grid size ${gridSize} is even, using ${gridSize + 1} instead`);
    gridSize = gridSize + 1;
  }

  const coordinates = [];
  const k = Math.floor(gridSize / 2); // [-k..k] range
  
  // Convert meters to degrees
  const dLat = stepMeters / 111320;
  const dLng = stepMeters / (111320 * Math.cos(centerLat * Math.PI / 180));
  
  logger.log(`🎯 Grid Generation: ${gridSize}x${gridSize}, step=${stepMeters}m, radius=${searchRadiusMeters}m`);
  logger.log(`📐 Degree conversion: dLat=${dLat.toFixed(8)}, dLng=${dLng.toFixed(8)}`);
  
  // Generate grid coordinates: For i,j in [-k..k]
  for (let i = -k; i <= k; i++) {
    for (let j = -k; j <= k; j++) {
      const lat = centerLat + i * dLat;
      const lng = centerLng + j * dLng;
      const gpsCoordinates = `${lat.toFixed(6)},${lng.toFixed(6)},${searchRadiusMeters}`;
      
      coordinates.push({
        i: i,
        j: j,
        lat: parseFloat(lat.toFixed(6)),
        lng: parseFloat(lng.toFixed(6)),
        gpsCoordinates: gpsCoordinates,
        cellId: `cell-${i}-${j}`,
        tag: `cell-${i}-${j}`
      });
    }
  }
  
  logger.log(`📍 Generated ${coordinates.length} grid points`);
  return coordinates;
}

// Helper function to calculate distance between two coordinates in kilometers
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// DataForSEO Client Service
export class DataForSEOPlacesService {
  constructor(credentials) {
    // Handle credentials in "login:password" format or use environment variables
    if (typeof credentials === 'string' && credentials.includes(':')) {
      const [login, password] = credentials.split(':');
      this.login = login;
      this.password = password;
      this.login = DATAFORSEO_LOGIN || credentials;
      this.password = DATAFORSEO_PASSWORD || '';
    }
    
    this.primaryCategoryFilter = null;
    this.useLocalFinder = false; // Default to Google Maps
    this.maxRetries = 3;
    this.callCount = 0;
    this.estimatedCredits = 0;
  }

  /**
   * Format coordinates to max 7 decimal places as per DataForSEO requirements
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {string} Formatted "lat,lng" with max 7 decimals
   */
  formatCoordinates(lat, lng) {
    const formatNumber = (num) => {
      const rounded = Number(num.toFixed(7));
      return rounded.toString();
    };
    return `${formatNumber(lat)},${formatNumber(lng)}`;
  }

  // No longer need authorization header - backend handles authentication
  getAuthHeader() {
    return null; // Backend handles DataForSEO authentication
  }
  // Get the appropriate endpoint based on search type
  getEndpoint() {
    return this.useLocalFinder ? LOCAL_FINDER_ENDPOINT : GOOGLE_MAPS_ENDPOINT;
  }

  // Set endpoint type: 'maps' or 'local_finder'
  setEndpointType(type) {
    this.useLocalFinder = type === 'local_finder';
    logger.log(`🔄 Endpoint set to: ${type}`);
  }

  // Main grid runner - fetchGrid implementation
  async fetchGrid(config) {
    const {
      endpointType = 'maps', // 'maps' or 'local_finder'
      keyword,
      centerLat,
      centerLng,
      gridSize = 7,
      stepMeters = 1000,
      searchRadiusMeters = 800,
      targetBusiness,
      maxCrawlPages = 3,
      languageCode = 'en',
      concurrency = 8,  // Reduced from 15 to avoid timeout issues
      dryRun = false,
      device = 'desktop',
      os = 'windows',
      minRating = null,
      timeFilter = null
    } = config;

    // Set endpoint type
    this.setEndpointType(endpointType);
    this.concurrency = concurrency;
    
    logger.log('🎯 Starting DataForSEO Grid Rank Tracking:');
    logger.log(`   Keyword: "${keyword}"`);
    logger.log(`   Center: ${centerLat}, ${centerLng}`);
    logger.log(`   Grid: ${gridSize}x${gridSize}, step=${stepMeters}m, radius=${searchRadiusMeters}m`);
    logger.log(`   Endpoint: ${endpointType}`);
    logger.log(`   Concurrency: ${concurrency}`);

    // Generate grid coordinates
    const gridCoordinates = generateGrid(centerLat, centerLng, gridSize, stepMeters, searchRadiusMeters);
    
    if (dryRun) {
      logger.log('🧪 DRY RUN MODE - Grid generated, no API calls made');
      return {
        dryRun: true,
        totalCells: gridCoordinates.length,
        estimatedCalls: gridCoordinates.length,
        estimatedCredits: gridCoordinates.length * maxCrawlPages,
        gridCoordinates: gridCoordinates
      };
    }

    // Reset counters
    this.callCount = 0;
    this.estimatedCredits = 0;

    const results = [];
    const rawResponses = [];
    
    // Process grid with concurrency control
    const chunks = this.chunkArray(gridCoordinates, this.concurrency);
    
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      logger.log(`🔄 Processing chunk ${chunkIndex + 1}/${chunks.length} (${chunk.length} cells)`);
      
      const chunkPromises = chunk.map(coord => 
        this.processSingleCell(keyword, coord, targetBusiness, {
          maxCrawlPages,
          languageCode,
          device,
          os,
          minRating,
          timeFilter
        })
      );
      
      const chunkResults = await Promise.allSettled(chunkPromises);
      
      chunkResults.forEach((result, index) => {
        const coord = chunk[index];
        if (result.status === 'fulfilled') {
          results.push(result.value.summary);
          rawResponses.push(result.value.raw);
        } else {
          logger.error(`❌ Error processing cell ${coord.cellId}:`, result.reason);
          results.push({
            ...coord,
            rank: null,
            found: false,
            error: result.reason.message,
            requestedAt: new Date().toISOString()
          });
        }
      });
      
      // Rate limiting between chunks
      if (chunkIndex < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    const summary = this.generateGridSummary(results);
    
    logger.log(`✅ Grid analysis complete:`);
    logger.log(`   Total calls: ${this.callCount}`);
    logger.log(`   Estimated credits: ${this.estimatedCredits}`);
    logger.log(`   Found in ${summary.foundCount}/${summary.totalCells} cells`);
    logger.log(`   Average rank: ${summary.averageRank?.toFixed(1) || 'N/A'}`);

    return {
      keyword,
      targetBusiness,
      endpointType,
      gridSize,
      stepMeters,
      searchRadiusMeters,
      centerCoordinates: { lat: centerLat, lng: centerLng },
      results,
      rawResponses,
      summary,
      callCount: this.callCount,
      estimatedCredits: this.estimatedCredits
    };
  }

  extractCoordinatesFromItem(item) {
    if (!item) {
      return { lat: null, lng: null };
    }

    if (item.gps_coordinates) {
      if (typeof item.gps_coordinates === 'object') {
        return {
          lat: parseFloat(item.gps_coordinates.latitude ?? item.gps_coordinates.lat ?? NaN),
          lng: parseFloat(item.gps_coordinates.longitude ?? item.gps_coordinates.lng ?? NaN)
        };
      }

      if (typeof item.gps_coordinates === 'string') {
        const parts = item.gps_coordinates.split(',').map(part => parseFloat(part.trim()));
        if (parts.length >= 2 && parts.every(v => !Number.isNaN(v))) {
          return { lat: parts[0], lng: parts[1] };
        }
      }
    }

    if (typeof item.latitude === 'number' && typeof item.longitude === 'number') {
      return { lat: item.latitude, lng: item.longitude };
    }

    if (item.location) {
      return {
        lat: parseFloat(item.location.latitude ?? item.location.lat ?? NaN),
        lng: parseFloat(item.location.longitude ?? item.location.lng ?? NaN)
      };
    }

    return { lat: null, lng: null };
  }

  convertItemToBusiness(item, searchMethod = 'dataforseo', index = 0) {
    if (!item) {
      return null;
    }

    const { lat, lng } = this.extractCoordinatesFromItem(item);
    const rawPlaceId = item.place_id || item.placeId || item.cid || item.feature_id || item.fid || null;
    const categories = Array.isArray(item.categories) && item.categories.length
      ? item.categories
      : Array.isArray(item.types) && item.types.length
        ? item.types
        : item.category
          ? [item.category]
          : [];

    // Extract rating and review count from DataForSEO format
    const ratingValue = item.rating?.value ?? (typeof item.rating === 'number' ? Number(item.rating) : null);
    const votesCount = item.rating?.votes_count ?? item.reviews_count ?? item.user_ratings_total ?? item.reviews ?? 0;
    const photoCount = item.total_photos ?? 0;
    
    const business = {
      placeId: rawPlaceId ? String(rawPlaceId) : `dataforseo_${item.cid || item.feature_id || index}`,
      place_id: rawPlaceId ? String(rawPlaceId) : `dataforseo_${item.cid || item.feature_id || index}`,
      name: item.title || item.name || 'Unknown Business',
      title: item.title || item.name, // DataForSEO field
      address: item.address || item.full_address || '',
      latitude: typeof lat === 'number' && !Number.isNaN(lat) ? lat : null,
      longitude: typeof lng === 'number' && !Number.isNaN(lng) ? lng : null,
      // Rating fields - multiple formats for compatibility
      rating: ratingValue,
      reviewsCount: votesCount,
      reviewCount: votesCount, // Alternative field name
      userRatingCount: votesCount, // Google Places format
      user_ratings_total: votesCount, // Legacy format
      // Website fields - multiple formats for compatibility
      website: item.url || item.link || null,
      websiteUri: item.url || item.link || null, // Google Places format
      url: item.url || item.link || null, // DataForSEO field
      // Phone fields
      phone: item.phone || item.phone_number || null,
      internationalPhoneNumber: item.phone || item.phone_number || null,
      // Category fields
      primaryType: item.category || (categories.length ? categories[0] : null),
      category: item.category || (categories.length ? categories[0] : null), // DataForSEO field
      categories,
      types: categories,
      category_ids: item.category_ids || [], // DataForSEO field
      categoryIds: item.category_ids || [],
      additionalCategories: item.additional_categories || [],
      // Photo fields - multiple formats for compatibility
      totalPhotos: photoCount,
      photoCount: photoCount, // Alternative field name
      total_photos: photoCount, // DataForSEO field
      // Hours fields
      workHours: item.work_hours || null,
      work_hours: item.work_hours || null, // DataForSEO field
      openingHours: item.work_hours?.timetable ? [item.work_hours.timetable] : [],
      // Other fields
      cid: item.cid || null,
      featureId: item.feature_id || null,
      googleUrl: item.url || null,
      thumbnail: item.main_image || item.thumbnail || item.image_url || null,
      searchMethod,
      originalRank: item.rank_absolute || item.rank_group || item.position || item.rank || index + 1,
      // Additional DataForSEO fields
      addressInfo: item.address_info || null,
      ratingDistribution: item.rating_distribution || null,
      isClaimed: item.is_claimed ?? null,
      is_claimed: item.is_claimed ?? null, // DataForSEO field
      isDirectoryItem: item.is_directory_item ?? null,
      priceLevel: item.price_level || null,
      hotelRating: item.hotel_rating || null,
      localJustifications: item.local_justifications || null,
      domain: item.domain || null,
      contactUrl: item.contact_url || null,
      bookOnlineUrl: item.book_online_url || null,
      book_online_url: item.book_online_url || null, // DataForSEO field
      contributorUrl: item.contributor_url || null,
      rawItem: item
    };

    if (!business.categories.length && item.primary_category) {
      business.categories = [item.primary_category];
      business.primaryType = item.primary_category;
    }

    return business;
  }

  convertItemsToBusinesses(items, searchMethod = 'dataforseo') {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .map((item, index) => this.convertItemToBusiness(item, searchMethod, index))
      .filter(Boolean);
  }

  /**
   * Process a single grid cell with enhanced DataForSEO parameters
   * @param {string} keyword - Search keyword
   * @param {object} coord - Grid coordinate {lat, lng, tag}
   * @param {object} targetBusiness - Target business to find
   * @param {object} options - Enhanced options:
   *   - maxCrawlPages: Number of pages to crawl (default: 3, max: 100)
   *   - depth: Number of results in SERP (default: 100, max: 700)
   *   - searchThisArea: Enable "search this area" mode (default: true)
   *   - searchPlaces: Enable search places mode (default: true)
   *   - zoom: Map zoom level (default: 17z, range: 3z-21z)
   *   - languageCode: Language code (default: 'en')
   *   - device: Device type ('desktop' or 'mobile')
   *   - os: Operating system ('windows', 'macos', 'android', 'ios')
   */
  async processSingleCell(keyword, coord, targetBusiness, options) {
    const {
      maxCrawlPages = 3,
      languageCode = 'en',
      device = 'desktop',
      os = 'windows',
      minRating = null,
      timeFilter = null,
      depth = null,
      searchThisArea = false,  // Set to false to restrict to displayed area only
      searchPlaces = true,
      zoom = null
    } = options;

    // Format coordinates with max 7 decimal places per DataForSEO requirements
    const formattedCoords = this.formatCoordinates(coord.lat, coord.lng);
    const locationCoordinate = zoom 
      ? `${formattedCoords},${zoom}z`
      : formattedCoords;

    const requestBody = [{
      keyword: keyword,
      language_code: languageCode,
      location_coordinate: locationCoordinate,
      device: device,
      os: os,
      max_crawl_pages: maxCrawlPages,
      search_this_area: searchThisArea,
      search_places: searchPlaces,
      tag: coord.tag
    }];

    // Add depth parameter if specified
    if (depth && depth > 0) {
      requestBody[0].depth = Math.min(depth, 700); // Max 700 per docs
    }

    // Add optional fields for Local Finder
    if (this.useLocalFinder) {
      if (minRating) requestBody[0].min_rating = minRating;
      if (timeFilter) requestBody[0].time_filter = timeFilter;
    }

    const requestedAt = new Date().toISOString();
    
    try {
      const response = await this.makeRequestWithRetry(requestBody);
      this.callCount++;
      this.estimatedCredits += maxCrawlPages;

      const task = response.tasks[0];
      const result = task.result?.[0] || {};
      const items = result.items || [];
      
      // Extract additional response fields
      const metadata = {
        check_url: result.check_url,
        datetime: result.datetime,
        se_results_count: result.se_results_count,
        items_count: result.items_count,
        item_types: result.item_types || [],
        spell: result.spell,
        refinement_chips: result.refinement_chips
      };

      const businesses = this.convertItemsToBusinesses(
        items,
        this.useLocalFinder ? 'dataforseo_local_finder' : 'dataforseo_maps'
      );

      const rank = this.findRank(items, targetBusiness);

      const summary = {
        ...coord,
        rank: rank,
        found: rank !== null,
        metadata: metadata,
        endpointType: this.useLocalFinder ? 'local_finder' : 'maps',
        topResults: businesses,
        requestedAt: requestedAt
      };

      const raw = {
        cellTag: coord.tag,
        requestBody: requestBody[0],
        response: task,
        requestedAt: requestedAt
      };

      return { summary, raw };

    } catch (error) {
      logger.error(`❌ Error processing cell ${coord.cellId}:`, error);
      throw error;
    }
  }

  // Make API request with retry logic
  async makeRequestWithRetry(requestBody, attempt = 1) {
    try {
      const response = await fetch(this.getEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        // Handle different HTTP status codes
        if (response.status === 429 || response.status === 408) {
          // Rate limit or timeout - retry with exponential backoff
          if (attempt <= this.maxRetries) {
            const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
            logger.warn(`⏳ Rate limited, retrying in ${delay}ms (attempt ${attempt}/${this.maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return this.makeRequestWithRetry(requestBody, attempt + 1);
          }
        }
        
        if (response.status >= 500 && attempt <= this.maxRetries) {
          // Server error - retry
          const delay = Math.pow(2, attempt) * 1000;
          logger.warn(`🔄 Server error ${response.status}, retrying in ${delay}ms (attempt ${attempt}/${this.maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.makeRequestWithRetry(requestBody, attempt + 1);
        }
        
        throw new Error(`DataForSEO API request failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.tasks || data.tasks.length === 0) {
        throw new Error('No tasks returned from DataForSEO API');
      }

      const task = data.tasks[0];
      if (task.status_code !== 20000) {
        const statusMessage = task.status_message || '';
        logger.log(`🔍 DataForSEO status_code: ${task.status_code}, status_message: "${statusMessage}"`);
        // Handle "No Search Results" (and similar messages) as a normal case, not an error
        if (statusMessage.toLowerCase().includes('no search results') || 
            statusMessage.toLowerCase().includes('no results found') ||
            statusMessage.toLowerCase().includes('no data available')) {
          logger.log(`📍 No results found for this location (normal for some grid points)`);
          return {
            ...data,
            tasks: [
              {
                ...task,
                status_code: 20000,
                status_message: 'OK: No search results',
                result: task.result && task.result.length > 0
                  ? task.result.map(res => ({ ...res, items: res.items || [] }))
                  : [{ items: [] }]
              }
            ]
          };
        }
        throw new Error(`DataForSEO task failed: ${statusMessage}`);
      }

      return data;

    } catch (error) {
      if (attempt <= this.maxRetries && (error.name === 'TypeError' || error.message.includes('timeout'))) {
        // Network error - retry
        const delay = Math.pow(2, attempt) * 1000;
        logger.warn(`🌐 Network error, retrying in ${delay}ms (attempt ${attempt}/${this.maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequestWithRetry(requestBody, attempt + 1);
      }
      throw error;
    }
  }

  // Rank detection logic
  findRank(items, targetBusiness) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (targetBusiness.cid && item.cid === targetBusiness.cid) {
        return i + 1; // 1-based rank
      }
      if (targetBusiness.placeId && item.place_id === targetBusiness.placeId) {
        return i + 1;
      }
    }

    // Fallback: normalized name + address match

    if (targetBusiness.name) {
      const targetName = this.normalizeBusinessName(targetBusiness.name);
      const targetAddress = targetBusiness.address ? this.normalizeAddress(targetBusiness.address) : null;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const itemName = this.normalizeBusinessName(item.title || '');
        
        if (itemName && targetName && itemName.includes(targetName)) {
          // Name match found, verify with address if available
          if (targetAddress && item.address) {
            const itemAddress = this.normalizeAddress(item.address);
            if (this.addressMatches(itemAddress, targetAddress)) {
              return i + 1;
            }
          } else {
            // Name match without address verification
            return i + 1;
          }
        }
      }
    }

    return null; // Not found within fetched depth
  }

  // Normalize business name (case/whitespace-insensitive, strip common suffixes)
  normalizeBusinessName(name) {
    if (!name) return '';
    
    return name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/\b(inc|llc|ltd|corp|corporation|company|co|llp|lp)\b\.?$/gi, '')
      .replace(/[^\w\s]/g, '')
      .trim();
  }

  // Normalize address
  normalizeAddress(address) {
    if (!address) return '';
    
    return address
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s]/g, ' ')
      .trim();
  }

  // Check if addresses match (flexible matching)
  addressMatches(itemAddress, targetAddress) {
    if (!itemAddress || !targetAddress) return false;
    
    // Split addresses into components and check for overlap
    const itemParts = itemAddress.split(' ').filter(part => part.length > 2);
    const targetParts = targetAddress.split(' ').filter(part => part.length > 2);
    
    // Require at least 2 matching components
    let matches = 0;
    for (const targetPart of targetParts) {
      if (itemParts.some(itemPart => itemPart.includes(targetPart) || targetPart.includes(itemPart))) {
        matches++;
      }
    }
    
    return matches >= 2;
  }

  // Utility: chunk array for concurrency control
  chunkArray(array, chunkSize) {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  // Generate grid summary statistics
  generateGridSummary(results) {
    const found = results.filter(r => r.found);
    const notFound = results.filter(r => !r.found);
    const errors = results.filter(r => r.error);

    const ranks = found.map(r => r.rank).filter(r => r !== null);
    
    return {
      totalCells: results.length,
      foundCount: found.length,
      notFoundCount: notFound.length,
      errorCount: errors.length,
      averageRank: ranks.length > 0 ? ranks.reduce((a, b) => a + b, 0) / ranks.length : null,
      bestRank: ranks.length > 0 ? Math.min(...ranks) : null,
      worstRank: ranks.length > 0 ? Math.max(...ranks) : null,
      topThreeCount: ranks.filter(r => r <= 3).length,
      topTenCount: ranks.filter(r => r <= 10).length,
      foundPercentage: results.length > 0 ? (found.length / results.length) * 100 : 0
    };
  }

  // Single cell test methods
  async testSingleCellLocalFinder(keyword, lat, lng, radius = 800, maxCrawlPages = 3) {
    this.setEndpointType('local_finder');
    
    const coord = {
      lat,
      lng,
      gpsCoordinates: `${lat},${lng},${radius}`,
      cellId: 'test-cell',
      tag: 'test-cell'
    };

    logger.log(`🧪 Testing single cell (Local Finder): "${keyword}" at ${lat},${lng} (${radius}m)`);
    
    try {
      const result = await this.processSingleCell(keyword, coord, null, {
        maxCrawlPages,
        languageCode: 'en',
        device: 'desktop',
        os: 'windows'
      });
      
      logger.log(`✅ Test successful: Found ${result.summary.topResults.length} results`);
      return result;
    } catch (error) {
      logger.error('❌ Test failed:', error);
      throw error;
    }
  }

  async testSingleCellMaps(keyword, lat, lng, radius = 800, maxCrawlPages = 3) {
    this.setEndpointType('maps');
    
    const coord = {
      lat,
      lng,
      gpsCoordinates: `${lat},${lng},${radius}`,
      cellId: 'test-cell',
      tag: 'test-cell'
    };

    logger.log(`🧪 Testing single cell (Maps): "${keyword}" at ${lat},${lng} (${radius}m)`);
    
    try {
      const result = await this.processSingleCell(keyword, coord, null, {
        maxCrawlPages,
        languageCode: 'en',
        device: 'desktop',
        os: 'windows'
      });
      
      logger.log(`✅ Test successful: Found ${result.summary.topResults.length} results`);
      return result;
    } catch (error) {
      logger.error('❌ Test failed:', error);
      throw error;
    }
  }

  // Compatibility methods for existing GooglePlacesService interface
  
  async validateApiKey() {
    try {
      const response = await this.testSingleCellMaps('test', 40.7128, -74.0060, 800, 1);
      return response !== null;
    } catch (error) {
      logger.error('DataForSEO API validation error:', error);
      return false;
    }
  }

  async textSearch(query, location) {
    // Convert to single-cell grid search for compatibility
    const result = await this.fetchGrid({
      endpointType: 'maps',
      keyword: query,
      centerLat: location.lat,
      centerLng: location.lng,
      gridSize: 1,
      stepMeters: 0,
      searchRadiusMeters: 1000,
      targetBusiness: { name: query },
      maxCrawlPages: 3,
      concurrency: 1
    });
    
    // Convert to expected format
    return {
      results: result.results.map(r => ({
        place_id: `dataforseo_${r.cellId}`,
        name: query,
        geometry: {
          location: { lat: r.lat, lng: r.lng }
        },
        rating: null,
        user_ratings_total: null,
        vicinity: `${r.lat}, ${r.lng}`,
        rank: r.rank,
        topResults: r.topResults
      }))
    };
  }

  async enhancedSearch(query, location, options = {}) {
    const result = await this.fetchGrid({
      endpointType: options.endpointType || 'maps',
      keyword: query,
      centerLat: location.lat,
      centerLng: location.lng,
      gridSize: options.gridSize || 5,
      stepMeters: options.stepMeters || 500,
      searchRadiusMeters: options.searchRadiusMeters || 800,
      targetBusiness: options.targetBusiness || { name: query },
      maxCrawlPages: options.maxCrawlPages || 3,
      concurrency: options.concurrency || 8,  // Reduced from 15 to avoid timeout issues
      languageCode: options.languageCode || 'en'
    });
    
    return result;
  }

  async enhancedPlacesAPISearch(location, radiusKm, keyword, targetBusinessName, address, centerLocation, searchRadius, options = {}) {
    const searchRadiusMeters = Math.round((radiusKm ?? 1) * 1000);
    const {
      depth = null,
      searchThisArea = false,  // Set to false to restrict to displayed area only
      searchPlaces = true,
      zoom = 17
    } = options;

    // Format coordinates with max 7 decimal places per DataForSEO requirements
    const formattedCoords = this.formatCoordinates(location.lat, location.lng);

    const request = [{
      keyword,
      language_code: 'en',
      location_coordinate: `${formattedCoords},${zoom}z`,
      device: 'desktop',
      os: 'windows',
      max_crawl_pages: 3,
      search_this_area: searchThisArea,
      search_places: searchPlaces,
      tag: `enhanced-${location.lat}-${location.lng}`
    }];

    if (depth && depth > 0) {
      request[0].depth = Math.min(depth, 700);
    }

    const response = await this.makeRequestWithRetry(request);
    this.callCount++;
    this.estimatedCredits += 3;

    const items = response.tasks?.[0]?.result?.[0]?.items || [];
    const businesses = this.convertItemsToBusinesses(
      items,
      this.useLocalFinder ? 'dataforseo_local_finder' : 'dataforseo_maps'
    );

    if (targetBusinessName || address || centerLocation) {
      businesses.forEach(business => {
        business.distanceFromCenter = centerLocation && business.latitude && business.longitude
          ? calculateDistance(centerLocation.lat, centerLocation.lng, business.latitude, business.longitude)
          : null;
        business.searchRadius = searchRadius ?? radiusKm ?? null;
      });
    }

    return businesses;
  }

  async nearbySearch(location, radiusMeters, keyword, options = {}) {
    const {
      depth = null,
      searchThisArea = false,  // Set to false to restrict to displayed area only
      searchPlaces = true,
      zoom = 17
    } = options;

    // Format coordinates with max 7 decimal places per DataForSEO requirements
    const formattedCoords = this.formatCoordinates(location.lat, location.lng);

    const request = [{
      keyword,
      language_code: 'en',
      location_coordinate: `${formattedCoords},${zoom}z`,
      device: 'desktop',
      os: 'windows',
      max_crawl_pages: 2,
      search_this_area: searchThisArea,
      search_places: searchPlaces,
      tag: `nearby-${location.lat}-${location.lng}`
    }];

    if (depth && depth > 0) {
      request[0].depth = Math.min(depth, 700);
    }

    const response = await this.makeRequestWithRetry(request);
    this.callCount++;
    this.estimatedCredits += 2;

    const items = response.tasks?.[0]?.result?.[0]?.items || [];
    return this.convertItemsToBusinesses(
      items,
      this.useLocalFinder ? 'dataforseo_local_finder_nearby' : 'dataforseo_maps_nearby'
    );
  }

  async textSearchNew(query, location, radiusMeters = 1000) {
    return this.enhancedPlacesAPISearch(location, radiusMeters / 1000, query);
  }

  async textSearchLegacy(options = {}) {
    const { query, location, radius } = options;
    if (!location || typeof location.lat !== 'number' || typeof location.lng !== 'number') {
      return [];
    }
    return this.enhancedPlacesAPISearch(location, (radius ?? 1000) / 1000, query);
  }

  async getPlaceDetails(placeId) {
    if (!placeId) return null;
    try {
      const request = [{
        keyword: placeId,
        language_code: 'en',
        location_coordinate: `0,0`,
        device: 'desktop',
        os: 'windows',
        max_crawl_pages: 1,
        tag: `details-${placeId}`
      }];

      const response = await this.makeRequestWithRetry(request);
      const items = response.tasks?.[0]?.result?.[0]?.items || [];
      const match = items.find(item => String(item.place_id) === String(placeId) || String(item.cid) === String(placeId));
      if (!match) return null;
      return this.convertItemToBusiness(match, 'dataforseo_details', 0);
    } catch (error) {
      logger.warn('DataForSEO getPlaceDetails fallback:', error.message);
      return null;
    }
  }

  setPrimaryCategoryFilter(category) {
    this.primaryCategoryFilter = category;
  }

  filterByPrimaryCategory(results) {
    if (!this.primaryCategoryFilter) return results;
    
    // Normalize the filter category (e.g., "electronics store" -> "electronic")
    const filterBase = this.primaryCategoryFilter.toLowerCase()
      .replace(/\s+(store|shop|showroom|retail|center|service|dealer|outlet)\s*$/i, '')
      .trim();
    
    logger.log(`🔍 Filtering with base category: "${filterBase}" (from "${this.primaryCategoryFilter}")`);
    logger.log(`📊 Total results before filtering: ${results.length}`);
    
    const filtered = results.filter(result => {
      const types = result.types || [];
      const categories = result.categories || [];
      const primaryType = result.primaryType || '';
      const allCategories = [...types, ...categories, primaryType].filter(Boolean);
      
      logger.log(`\n🏢 Checking: ${result.name}`);
      logger.log(`   Categories: ${allCategories.join(', ')}`);
      
      // Check if any category matches the base filter
      const matches = allCategories.some(cat => {
        const normalizedCat = cat.toLowerCase()
          .replace(/\s+(store|shop|showroom|retail|center|service|dealer|outlet)\s*$/i, '')
          .trim();
        
        logger.log(`   Comparing: "${normalizedCat}" with "${filterBase}"`);
        
        // STRICT matching: the category must contain the filter base
        const isMatch = normalizedCat.includes(filterBase);
        
        if (isMatch) {
          logger.log(`   ✅ MATCH FOUND: "${normalizedCat}" contains "${filterBase}"`);
        }
        
        return isMatch;
      });
      
      if (!matches) {
        logger.log(`   ❌ FILTERED OUT: No matching category found`);
      } else {
        logger.log(`   ✅ KEPT: Category matches filter`);
      }
      
      return matches;
    });
    
    logger.log(`\n📊 Filtering complete: ${filtered.length}/${results.length} results kept`);
    return filtered;
  }

  async getPlaceDetailsForEnrichment(placeId) {
    // For DataForSEO, we don't have separate place details
    // Return minimal details structure
    return {
      place_id: placeId,
      name: 'Business Name',
      formatted_address: 'Address not available',
      geometry: {
        location: { lat: 0, lng: 0 }
      },
      rating: null,
      user_ratings_total: null
    };
  }

  async testApiConnection() {
    return await this.validateApiKey();
  }

  // Legacy method compatibility
  async performGridRankTracking(config) {
    return await this.fetchGrid({
      endpointType: 'maps',
      keyword: config.keyword,
      centerLat: config.centerLocation.lat,
      centerLng: config.centerLocation.lng,
      gridSize: config.gridSize || 5,
      stepMeters: (config.searchRadius * 1000) / config.gridSize || 500,
      searchRadiusMeters: 800,
      targetBusiness: { name: config.businessName },
      maxCrawlPages: 3,
      concurrency: 8  // Reduced from 15 to avoid timeout issues
    });
  }
}

// Export as GooglePlacesService for compatibility
export class GooglePlacesService extends DataForSEOPlacesService {
  constructor(credentials, useNewAPI = true) {
    super(credentials);
    this.useNewAPI = useNewAPI;
  }
}
